import { memo, useCallback } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { isBasicContentImproverTool } from 'src/data/generationTool';
import { useFreshRef } from 'src/lib/hooks';

import { FormContext } from '../../components/FormContext';
import { FormGeneralContext } from '../../components/FormGeneralContext';
import { useDataContextValue } from '../../context';

import { useStyles } from './useStyles';

const DEFAULT_VALUE = {};

const getContentComponent = (generationTool) => {
  if (isBasicContentImproverTool(generationTool)) {
    return FormContext;
  }
  return FormGeneralContext;
};

export const ContentContext = memo(
  ({ className, value, onChange, onFieldFocus, onFieldBlur, errors, disabled }) => {
    const styles = useStyles();
    const { viewMode, generationTool } = useDataContextValue();

    const Component = getContentComponent(generationTool);

    const valueRef = useFreshRef(value);

    const handleFieldChange = useCallback(
      (field, fieldValue) => {
        const newValue = {
          ...valueRef.current,
          [field]: fieldValue,
        };
        onChange(newValue, field);
      },
      [onChange, valueRef]
    );

    return (
      <div
        className={cx(styles.root, className)}
        data-view-mode={viewMode}
        data-testid="ContentContext"
      >
        <Component
          content={value || DEFAULT_VALUE}
          onFieldChange={handleFieldChange}
          onFieldFocus={onFieldFocus}
          onFieldBlur={onFieldBlur}
          errors={errors}
          disabled={disabled}
        />
      </div>
    );
  }
);

ContentContext.propTypes = {
  className: PropTypes.string,
  value: PropTypes.shape(),
  onChange: PropTypes.func.isRequired,
  onFieldFocus: PropTypes.func,
  onFieldBlur: PropTypes.func,
  errors: PropTypes.shape(),
  disabled: PropTypes.bool,
};
ContentContext.defaultProps = {
  className: null,
  value: null,
  onFieldFocus: null,
  onFieldBlur: null,
  errors: null,
  disabled: false,
};
